import type { Adapter, AdapterConfig } from "./adapter";
import { registerAdapter } from "./adapter";
import type { BusPayload, ConnectionState } from "../core/messages";

/**
 * Direct browser MQTT: WSS for brokers, or WS on loopback during HTTP development.
 * `topic="root/#"` maps root/signal ↔ device/signal. Other wildcard filters keep
 * the full incoming wire topic and need `publish-topic` to enable commands.
 * `publish-topic` overrides only the outbound prefix, never the subscription.
 * Optional attributes: username, password, clientid, qos (0–2), retain (false).
 * Credentials belong to the browser session; this adapter does not store them.
 */

type Qos = 0 | 1 | 2;
type SubscriptionCallback = (error: Error | null, granted?: Array<{ topic: string; qos: number }>) => void;

export interface MqttClientLike {
  readonly connected: boolean;
  on(event: "connect" | "reconnect" | "close" | "offline", cb: () => void): void;
  on(event: "message", cb: (topic: string, payload: Uint8Array) => void): void;
  on(event: "error", cb: (err: Error) => void): void;
  subscribe(topic: string, options: { qos: Qos }, callback: SubscriptionCallback): void;
  publish(topic: string, payload: string, options: { qos: Qos; retain: boolean }, callback: (error?: Error) => void): void;
  end(force?: boolean): void;
}

export interface MqttModuleLike {
  connect(url: string, opts: Record<string, unknown>): MqttClientLike;
}

export function mqttArtifactUrl(sdkUrl?: string): string {
  const current =
    typeof document !== "undefined" &&
    typeof HTMLScriptElement !== "undefined" &&
    document.currentScript instanceof HTMLScriptElement
    ? document.currentScript.src
    : "";
  const fallback = typeof document !== "undefined"
    ? new URL("/playground/sdk/granrojo.js", document.baseURI).href
    : "http://localhost/playground/sdk/granrojo.js";
  const url = new URL(sdkUrl || current || fallback);
  const match = /granrojo(?:\.([a-f0-9]{16,64}))?\.js$/.exec(url.pathname);
  const filename = match?.[1] ? `mqtt.${match[1]}.js` : "mqtt.js";
  url.pathname = url.pathname.replace(/granrojo(?:\.[a-f0-9]{16,64})?\.js$/, filename);
  url.search = "";
  url.hash = "";
  return url.href;
}

const MQTT_ARTIFACT_URL = mqttArtifactUrl();
let mqttModulePromise: Promise<MqttModuleLike> | null = null;

function loadMqtt(): Promise<MqttModuleLike> {
  if (mqttModulePromise) return mqttModulePromise;
  mqttModulePromise = new Promise<MqttModuleLike>((resolve, reject) => {
    const existing = (globalThis as Record<string, unknown>).mqtt;
    if (existing && typeof (existing as MqttModuleLike).connect === "function") {
      resolve(existing as MqttModuleLike);
      return;
    }
    const script = document.createElement("script");
    script.src = MQTT_ARTIFACT_URL;
    script.onload = () => {
      const mod = (globalThis as Record<string, unknown>).mqtt as MqttModuleLike | undefined;
      if (typeof mod?.connect === "function") resolve(mod);
      else {
        script.remove();
        reject(new Error("MQTT.js loaded but window.mqtt is missing"));
      }
    };
    script.onerror = () => {
      script.remove();
      reject(new Error("Failed to load MQTT.js. Check your connection and try again."));
    };
    document.head.appendChild(script);
  }).catch((error) => {
    // A transient artifact load failure must not poison later Connect attempts.
    mqttModulePromise = null;
    throw error;
  });
  return mqttModulePromise;
}

function parsePayload(bytes: Uint8Array): BusPayload {
  const text = new TextDecoder().decode(bytes).trim();
  if (text.startsWith("{") || text.startsWith("[")) {
    try {
      return JSON.parse(text) as BusPayload;
    } catch {
      return text;
    }
  }
  if (text !== "" && !Number.isNaN(Number(text))) return Number(text);
  if (text === "true") return true;
  if (text === "false") return false;
  return text;
}

function validTopicText(topic: string): boolean {
  return topic.length > 0 && !topic.includes("\0") && new TextEncoder().encode(topic).length <= 65535;
}

function validPublishTopic(topic: string): boolean {
  return validTopicText(topic) && !/[+#]/.test(topic);
}

function validFilter(topic: string): boolean {
  if (!validTopicText(topic)) return false;
  const parts = topic.split("/");
  if (parts[0] === "$share" && (parts.length < 3 || !parts[1] || /[+#]/.test(parts[1]))) return false;
  return parts.every((part, index) =>
    (!part.includes("+") || part === "+") &&
    (!part.includes("#") || (part === "#" && index === parts.length - 1)),
  );
}

function settings(options: Record<string, string>, pageProtocol: string) {
  const { url, topic } = options;
  if (!url || !topic) throw new Error("MQTT requires a broker url and subscription topic.");
  let endpoint: URL;
  try {
    endpoint = new URL(url);
  } catch {
    throw new Error("MQTT broker URL must be a valid wss:// URL.");
  }
  if (endpoint.protocol !== "wss:" && endpoint.protocol !== "ws:") {
    throw new Error("Browser MQTT requires a ws:// or wss:// WebSocket endpoint, not a TCP MQTT port.");
  }
  if (endpoint.username || endpoint.password || endpoint.hash) {
    throw new Error("Use the MQTT username/password fields; the broker URL cannot contain credentials or a fragment.");
  }
  if (endpoint.protocol === "ws:") {
    const loopback = endpoint.hostname === "localhost" || endpoint.hostname === "[::1]" || /^127\.\d+\.\d+\.\d+$/.test(endpoint.hostname);
    if (pageProtocol !== "http:" || !loopback) {
      throw new Error("Use wss:// for MQTT. ws:// is only available for a loopback broker on an HTTP development page.");
    }
  }
  if (!validFilter(topic)) throw new Error("Invalid MQTT subscription filter: + occupies one level; # must be the final level.");
  const qosText = options.qos ?? "0";
  if (!/^[012]$/.test(qosText)) throw new Error("MQTT qos must be 0, 1, or 2.");
  const qos = Number(qosText) as Qos;
  const retainText = options.retain;
  if (retainText !== undefined && !["", "true", "false", "1", "0"].includes(retainText)) {
    throw new Error("MQTT retain must be true or false.");
  }
  const retain = retainText === "" || retainText === "true" || retainText === "1";
  const root = !topic.startsWith("$share/") && !/[+#]/.test(topic.replace(/\/#$/, ""))
    ? topic.replace(/\/#$/, "").replace(/\/$/, "") || null
    : null;
  const publishRoot = options["publish-topic"] || root;
  if (publishRoot !== null && !validPublishTopic(publishRoot)) {
    throw new Error("MQTT publish-topic must be a nonempty prefix without + or # wildcards.");
  }
  return { url, topic, qos, retain, root, publishRoot };
}

export class MqttAdapter implements Adapter {
  state: ConnectionState = "idle";
  private client: MqttClientLike | null = null;
  private unsubscribe: (() => void) | null = null;
  private generation = 0;
  private readonly srcTag: string;

  constructor(
    private config: AdapterConfig,
    private loadModule: () => Promise<MqttModuleLike> = loadMqtt,
    private pageProtocol: () => string = () => typeof location === "undefined" ? "https:" : location.protocol,
  ) {
    this.srcTag = `adapter:${config.device}`;
  }

  async connect(): Promise<void> {
    const generation = ++this.generation;
    this.releaseClient();
    let parsed: ReturnType<typeof settings>;
    let mqtt: MqttModuleLike;
    try {
      parsed = settings(this.config.options, this.pageProtocol());
      this.setState("connecting");
      mqtt = await this.loadModule();
    } catch (error) {
      if (generation === this.generation) this.setState("error", error instanceof Error ? error.message : String(error));
      return;
    }
    // Disconnect, removal, or a newer Connect may have happened during CDN load.
    if (generation !== this.generation) return;

    const { url, topic, qos, retain, root, publishRoot } = parsed;
    const options: Record<string, unknown> = {
      clean: true,
      connectTimeout: 8000,
      queueQoSZero: false,
      // We resubscribe ourselves to check every SUBACK, including after reconnect.
      resubscribe: false,
    };
    for (const key of ["username", "password"] as const) {
      if (this.config.options[key]) options[key] = this.config.options[key];
    }
    if (this.config.options.clientid) options.clientId = this.config.options.clientid;
    let client: MqttClientLike;
    try {
      client = mqtt.connect(url, options);
    } catch (error) {
      this.setState("error", error instanceof Error ? error.message : String(error));
      return;
    }
    this.client = client;
    const current = () => generation === this.generation && this.client === client;
    let subscription = 0;
    let ready = false;

    client.on("connect", () => {
      if (!current()) return;
      const attempt = ++subscription;
      ready = false;
      this.setState("connecting", "Subscribing…");
      const subscribed: SubscriptionCallback = (error, granted) => {
        if (!current() || attempt !== subscription || !client.connected) return;
        if (error || granted?.length !== 1 || granted[0].topic !== topic || ![0, 1, 2].includes(granted[0].qos)) {
          this.releaseClient();
          this.setState("error", error?.message ?? "The MQTT broker rejected the subscription.");
          return;
        }
        ready = true;
        this.setState("connected");
        this.config.onDescriptor?.(null);
      };
      try {
        client.subscribe(topic, { qos }, subscribed);
      } catch (error) {
        subscribed(error instanceof Error ? error : new Error(String(error)));
      }
    });
    client.on("message", (wireTopic, payload) => {
      if (!current() || !client.connected) return;
      const signal = root !== null && wireTopic.startsWith(root + "/")
        ? wireTopic.slice(root.length + 1)
        : wireTopic;
      // Broker data must not impersonate the SDK's local $state/$descriptor bus.
      if (signal.startsWith("$")) return;
      this.config.bus.publish(`${this.config.device}/${signal}`, parsePayload(payload), { src: this.srcTag });
    });
    const reconnecting = () => {
      if (!current()) return;
      ready = false;
      subscription++;
      this.setState("connecting", "Reconnecting…");
    };
    client.on("close", reconnecting);
    client.on("offline", reconnecting);
    client.on("reconnect", reconnecting);
    client.on("error", (error) => {
      if (!current()) return;
      ready = false;
      subscription++;
      this.setState("error", error.message);
    });

    this.unsubscribe = this.config.bus.subscribe(`${this.config.device}/#`, (msg) => {
      // UI commands are live intent: never queue them while offline or before SUBACK.
      if (!current() || !ready || !client.connected || publishRoot === null || msg.src === this.srcTag) return;
      const prefix = `${this.config.device}/`;
      if (!msg.topic.startsWith(prefix)) return;
      const signal = msg.topic.slice(prefix.length);
      if (!signal || signal.startsWith("$")) return;
      const wireTopic = `${publishRoot}/${signal}`;
      if (!validPublishTopic(wireTopic)) return;
      const body = typeof msg.payload === "object" && msg.payload !== null ? JSON.stringify(msg.payload) : String(msg.payload);
      try {
        client.publish(wireTopic, body, { qos, retain }, (error) => {
          if (error && current()) this.setState("error", error.message);
        });
      } catch (error) {
        if (current()) this.setState("error", error instanceof Error ? error.message : String(error));
      }
    }, { replay: false });
  }

  async disconnect(): Promise<void> {
    this.generation++;
    this.releaseClient();
    this.setState("disconnected");
  }

  private releaseClient(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    const client = this.client;
    this.client = null;
    client?.end(true);
  }

  private setState(state: ConnectionState, detail?: string): void {
    this.state = state;
    this.config.onState(state, detail);
  }
}

registerAdapter({
  name: "mqtt",
  fields: [
    { name: "url", label: "Broker", placeholder: "wss://broker.example/mqtt", persist: true, cleanUrl: true },
    { name: "topic", label: "Subscribe topic", placeholder: "project/device/#", persist: true },
    { name: "publish-topic", label: "Publish prefix", placeholder: "project/device", persist: true },
    { name: "clientid", label: "Client ID", placeholder: "Optional unique client ID", persist: true },
    { name: "qos", label: "QoS", placeholder: "0", persist: true },
    { name: "retain", label: "Retain publishes", placeholder: "false", persist: true },
    { name: "username", label: "Username (this page only)", placeholder: "Optional username", trim: false },
    { name: "password", label: "Password (this page only)", placeholder: "Optional password", type: "password", trim: false },
  ],
  requiresGesture: false,
  supported: () => typeof WebSocket !== "undefined",
  create: (config) => new MqttAdapter(config),
});
